/* Story 13.6 apus-ami-agent (CN-F05, HA-F12) — "Kết nối tài khoản ERP" trong Hub: mỗi ERP hồ sơ `user-oauth-pkce` một dòng
   `connected | expired | not_connected` + nút; đề xuất bị `need_info` vì token ERP hết hạn ⇒ nút "Kết nối lại" đi ĐÚNG luồng 13.5.
   Hợp đồng: ami-agent-v1@1.1.0-draft `amiListErpLinks` · `amiStartErpLink` · `amiDisconnectErpLink` (chỉ Java). Server demo Node
   ⛔ có các đường ấy ⇒ `GET /erp-links` lỗi ⇒ danh sách rỗng ⇒ Hub ẩn nút. ⛔ bao giờ hiện / giữ token (server cũng ⛔ trả). */
import { el } from "../ui/dom.ts";

export type ErpLinkStatus = "connected" | "expired" | "not_connected";

export interface ErpLink {
  connectorId: string;
  erpName: string;
  status: ErpLinkStatus;
  linkedAt?: string | null;
}

export type ErpLinkAction = "connect" | "reconnect" | "disconnect";

/** Gọi API Hub (gốc `/api/v1/assistant/hub`) — cùng chữ ký `host.api` của Hub. */
export type HubApi = <T>(method: string, path: string, body?: unknown) => Promise<T>;

/** Cửa sổ bật tối thiểu mà luồng cần (để test thay được). */
export interface PopupLike {
  closed: boolean;
  close(): void;
  location: { href: string };
}

const STATUS: Record<ErpLinkStatus, { text: string; tone: string; action: ErpLinkAction; label: string }> = {
  connected: { text: "Đã kết nối", tone: "ok", action: "disconnect", label: "Ngắt kết nối" },
  expired: { text: "Hết hạn — cần kết nối lại", tone: "warn", action: "reconnect", label: "Kết nối lại" },
  not_connected: { text: "Chưa kết nối", tone: "info", action: "connect", label: "Kết nối" },
};

/** Lọc phản hồi `GET /erp-links` về đúng hình đã khai — khoá lạ (vd. token) bị bỏ, ⛔ đưa lên màn hình. */
export function normalizeErpLinks(body: unknown): ErpLink[] {
  const data = (body as { data?: unknown } | null)?.data;
  if (!Array.isArray(data)) return [];
  const out: ErpLink[] = [];
  for (const x of data) {
    const r = x as Partial<ErpLink> | null;
    if (!r || typeof r.connectorId !== "string" || !r.connectorId) continue;
    const status: ErpLinkStatus = r.status === "connected" || r.status === "expired" ? r.status : "not_connected";
    out.push({ connectorId: r.connectorId, erpName: typeof r.erpName === "string" && r.erpName ? r.erpName : r.connectorId, status, linkedAt: typeof r.linkedAt === "string" ? r.linkedAt : null });
  }
  return out;
}

/** Tải danh sách; mọi lỗi (Node demo 404, mạng) ⇒ rỗng — Hub chỉ ẩn tính năng, ⛔ báo lỗi cho người dùng. */
export async function loadErpLinks(api: HubApi): Promise<ErpLink[]> {
  try {
    return normalizeErpLinks(await api<unknown>("GET", "/erp-links"));
  } catch {
    return [];
  }
}

export function statusOf(s: ErpLinkStatus): { text: string; tone: string; action: ErpLinkAction; label: string } {
  return STATUS[s] || STATUS.not_connected;
}

/** Thanh trên: cần chú ý khi có ERP CHƯA/HẾT kết nối. */
export function needsAttention(links: ErpLink[]): number {
  return links.filter((l) => l.status !== "connected").length;
}

/** Popover "Tài khoản ERP": một dòng mỗi ERP. Nút mang `data-erp-act` + `data-erp-id` — Hub bắt bằng một listener chung. */
export function renderErpLinks(links: ErpLink[], busy: string | null = null, note: string | null = null): HTMLElement {
  const pop = el("div", "apx-bellpop apx-erppop", { role: "dialog", "aria-label": "Tài khoản ERP" });
  const head = el("div", "apx-bellhead");
  const title = el("strong");
  title.textContent = "Tài khoản ERP";
  head.append(title);
  pop.append(head);
  const hint = el("p", "apx-aside-hint");
  hint.textContent = "Ami ghi vào ERP bằng ĐÚNG quyền tài khoản của bạn. Kết nối một lần cho mỗi ERP.";
  pop.append(hint);
  for (const l of links) {
    const st = statusOf(l.status);
    const row = el("div", "apx-notif apx-erprow", { "data-erp-row": l.connectorId });
    const body = el("span", "apx-notif-body");
    const name = el("span", "apx-notif-title");
    name.textContent = l.erpName;
    const state = el("span", "apx-item-state apx-tone-" + st.tone);
    state.textContent = st.text;
    body.append(name, state);
    const b = el("button", "apx-btn apx-sm " + (st.action === "disconnect" ? "apx-btn-ghost" : "apx-btn-primary"), {
      type: "button",
      "data-erp-act": st.action,
      "data-erp-id": l.connectorId,
    });
    b.textContent = busy === l.connectorId ? "Đang mở…" : st.label;
    if (busy) b.setAttribute("disabled", "");
    row.append(body, b);
    pop.append(row);
  }
  if (note) {
    const n = el("p", "apx-aside-foot");
    n.textContent = note;
    pop.append(n);
  }
  return pop;
}

/** Đề xuất của TÔI bị dừng vì chưa/hết kết nối tài khoản ERP (13.5 AC2) ⇒ id kết nối để "Kết nối lại" (rỗng ⇒ không phải ca này). */
export function reconnectTarget(p: { state?: string; error?: string | null; erp?: { connectorId?: string } | null }, links: ErpLink[]): string | null {
  if (p.state !== "need_info" || p.error !== "erp_link_required") return null;
  const id = p.erp?.connectorId;
  if (id && links.some((l) => l.connectorId === id)) return id;
  const off = links.filter((l) => l.status !== "connected");
  return off.length === 1 ? off[0]!.connectorId : off[0]?.connectorId ?? links[0]?.connectorId ?? null;
}

/** URL uỷ quyền chỉ được là http(s) tuyệt đối — ⛔ `javascript:`/`data:` dù server có trả gì. */
export function safeAuthorizeUrl(u: unknown): string | null {
  if (typeof u !== "string") return null;
  try {
    const x = new URL(u);
    return x.protocol === "https:" || x.protocol === "http:" ? x.href : null;
  } catch {
    return null;
  }
}

/**
 * "Kết nối" / "Kết nối lại": cửa sổ bật PHẢI mở đồng bộ trong cú bấm (chặn popup) ⇒ bên gọi mở `about:blank` trước rồi đưa vào đây;
 * hàm xin URL uỷ quyền (`POST /erp-links/{id}/start` — server giữ verifier PKCE, ⛔ qua trình duyệt) rồi chuyển cửa sổ tới đó.
 * Lỗi ⇒ đóng cửa sổ, ném để Hub báo. ⛔ cửa sổ (trình duyệt chặn) ⇒ trả URL để Hub mở bằng liên kết.
 */
export async function startErpLink(api: HubApi, connectorId: string, popup: PopupLike | null): Promise<string> {
  let url: string | null;
  try {
    const r = await api<{ authorizeUrl?: string }>("POST", "/erp-links/" + encodeURIComponent(connectorId) + "/start", {});
    url = safeAuthorizeUrl(r?.authorizeUrl);
  } catch (err) {
    popup?.close();
    throw err;
  }
  if (!url) {
    popup?.close();
    throw new Error("ERP trả địa chỉ đăng nhập không hợp lệ");
  }
  if (popup && !popup.closed) popup.location.href = url;
  return url;
}

/** Chờ cửa sổ đóng (người dùng xong ở ERP / trang gọi về) rồi gọi `done` — Hub tải lại danh sách. Trả hàm huỷ. */
export function whenClosed(popup: PopupLike, done: () => void, everyMs = 800, maxMs = 15 * 60_000): () => void {
  const t0 = Date.now();
  const timer = setInterval(() => {
    if (popup.closed || Date.now() - t0 > maxMs) {
      clearInterval(timer);
      done();
    }
  }, everyMs);
  return () => clearInterval(timer);
}

export async function disconnectErpLink(api: HubApi, connectorId: string): Promise<void> {
  await api("DELETE", "/erp-links/" + encodeURIComponent(connectorId));
}
